/* eslint-disable react/display-name */
import React from 'react';
import dynamic from 'next/dynamic';

import AttachedTooltip, {
  AttachedTooltipProps,
} from 'react-scale/components/tour/ui/attachedTooltip';
import TooltipButton from 'react-scale/components/tour/ui/tooltipButton';
import logger from '@scale/frontend-lib/logging';

export const Finished = '__TourFinished';

type FinishedT = typeof Finished;

export function linearStepper<TStep>(steps: TStep[]): (step: TStep) => TStep | FinishedT {
  return step => {
    const nextStep = steps[steps.indexOf(step) + 1];
    if (nextStep) {
      return nextStep;
    }

    return Finished;
  };
}

interface TourConfig<TStep> {
  storageName: string;
  showing?: 'once' | 'always';
  isFinalStep: (step: TStep) => boolean;
  tooltipProps?: Partial<AttachedTooltipProps>;
}

interface TourTooltipContext<TStep> {
  currentStep: TStep | FinishedT;
  disabled: boolean;
  earlyExit?: boolean;
  nextStep: () => void;
  finish: () => void;
  setStep: (step: TStep) => void;
}

export interface TourTooltipProps<TStep> extends Omit<AttachedTooltipProps, 'show' | 'onClose'> {
  forStep: TStep;
  customExtra?: React.ReactElement;
  buttonText?: string;
  buttonIcon?: React.ReactElement;
  onActive?(): void;
  onStepDone?(): void;
  onClick?(e: React.MouseEvent): void;
}

export function createTour<TStep>(config: TourConfig<TStep>) {
  const Context = React.createContext<TourTooltipContext<TStep>>({
    currentStep: Finished,
    disabled: false,
    earlyExit: false,
    nextStep: () => {},
    finish: () => {},
    setStep: () => {},
  });

  Context.displayName = `TourContext-${config.storageName}`;

  function getStoreKey() {
    return `tour-${config.storageName}`;
  }

  function userHasSeenTour(): boolean {
    return !!localStorage.getItem(getStoreKey());
  }

  type TourProviderProps = {
    initialStep: TStep;
    getNextStep: (currentStep: TStep) => TStep | FinishedT;
    onFinish?: () => void;
    disabled?: boolean;
    earlyExit?: boolean;
    children?: React.ReactNode;
  };

  const TourProvider: React.FC<TourProviderProps> = props => {
    const { onFinish } = props;
    const [state, setState] = React.useState<TourTooltipContext<TStep>>({
      currentStep:
        (config.showing || 'always') === 'once' && userHasSeenTour() ? Finished : props.initialStep,
      disabled: props.disabled || false,
      earlyExit: props.earlyExit || false,
      nextStep: (): void => {
        setState(prev => {
          if (prev.currentStep === Finished) {
            return prev;
          }

          return {
            ...prev,
            currentStep: props.getNextStep(prev.currentStep),
          };
        });
      },
      finish: (): void => setState(prev => ({ ...prev, currentStep: Finished })),
      setStep: (step: TStep): void => setState(prev => ({ ...prev, currentStep: step })),
    });

    React.useEffect(() => {
      if (state.currentStep === Finished) {
        try {
          localStorage.setItem(getStoreKey(), 'true');
        } catch (error) {
          logger.error(`Failed to set local storage for ${getStoreKey()}`, error);
        }
        if (onFinish) {
          onFinish();
        }
      }
    }, [onFinish, state.currentStep]);

    return <Context.Provider value={state}>{props.children}</Context.Provider>;
  };

  const TourTooltip: React.FC<TourTooltipProps<TStep>> = React.memo(props => {
    const contextState = React.useContext(Context);

    if (contextState.disabled || contextState.currentStep === Finished) {
      return (props.children as React.ReactElement) || null;
    }

    const isFinalStep = config.isFinalStep(contextState.currentStep);

    const handleNextStep = () => {
      // trigger side effects when "next" is pressed
      contextState.nextStep();
      props.onStepDone && props.onStepDone();
    };

    const handleClose = () => {
      // do not trigger side effects when X is pressed
      if (contextState.earlyExit) {
        contextState.finish();
      } else {
        contextState.nextStep();
      }
    };

    const button = (
      <TooltipButton
        icon={props.buttonIcon}
        onClick={e => {
          if (props.onClick) {
            props.onClick(e);
          }
          e.stopPropagation();
          handleNextStep();
        }}
      >
        {props.buttonText || (isFinalStep ? 'Done' : 'Next')}
      </TooltipButton>
    );

    const extra = (
      <div className="extra">
        {props.customExtra || button}

        <style jsx>{`
          .extra {
            margin-top: 12px;
          }
        `}</style>
      </div>
    );

    return (
      <AttachedTooltip
        show={contextState.currentStep === props.forStep}
        onClose={handleClose}
        extra={extra}
        {...props}
        {...config.tooltipProps}
      />
    );
  });

  return {
    // Don't SSR due to localstorage usage
    Provider: dynamic(() => Promise.resolve(TourProvider), { ssr: false, loading: () => null }),
    Tooltip: dynamic(() => Promise.resolve(TourTooltip), { ssr: false, loading: () => null }),
    Context,
    hasUserSeenTour: userHasSeenTour,
    reset: () => {
      localStorage.deleteItem(getStoreKey());
    },
  };
}
